import React, { useState } from "react";
import { connect } from "react-redux";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import moment from "moment";
import { round } from "./utils";
import { ReactComponent as XlsxIcon } from "./excel.svg";
import { ReactComponent as ApiIcon } from "./images/qr_code.svg";
import { ReactComponent as EditIcon } from "./images/edit.svg";
import { ReactComponent as HideIcon } from "./images/visibility_off.svg";
import CurrentValueModal from "./CurrentValueModal";
import { MessageAction } from "./redux/actions/MessageActions";
import { SettingAction } from "./redux/actions/SettingActions";
import { FilterAction } from "./redux/actions/FilterActions";

require("moment/locale/fi");

const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  addMessage: (message, type) => dispatch(MessageAction.addMessage(message, type)),
  changeDeviceName: (deviceid, currentName) => () => {
    const enteredName = prompt("Anna anturille nimi", currentName);
    if (enteredName && enteredName.length > 0) {
      dispatch(SettingAction.changeDeviceName(deviceid, enteredName));
    }
  },
  toggleSensorFilter: (sensor) => () => dispatch(FilterAction.toggleSensorFilter(sensor)),
});

function Device(props) {
  const [currentValueModalOpen, setCurrentValueModalOpen] = useState(false);

  const { data, rawValues, showCalculated, changeDeviceName, toggleSensorFilter } = props;

  if (!data || !data.deviceid || !rawValues) return null;

  // Last measurement formatting
  let lastMeasurement = moment(data.timestamp, "x").format("HH:mm");
  if (new Date().valueOf() - parseInt(data.timestamp) > 18000000) {
    lastMeasurement = moment(data.timestamp, "x").format("D.M.YYYY HH:mm");
  }
  if (lastMeasurement === "Invalid date") {
    lastMeasurement = "!! Kytke anturi päälle !!";
  }

  // Data access check
  let dataUntil = data.until;
  if (dataUntil > 0) {
    if (dataUntil < 15719230590) dataUntil *= 1000;
    if (Date.now() > dataUntil) {
      return (
        <>
          <CurrentValueModal
            isOpen={currentValueModalOpen}
            onClose={() => setCurrentValueModalOpen(false)}
            deviceid={data.deviceid}
            apikey={data.api}
            changeApi={props.changeApi}
          />
          <div className="device-titles">
            <h2>{data.name || data.deviceid}</h2>
            <button className="btn-save" onClick={() => setCurrentValueModalOpen(true)}>
              <ApiIcon />
              Seuranta
            </button>
          </div>
          <div className="notification-outdated">
            <span className="message-text">Anturin vuosimaksut ovat erääntyneet. Ota yhteyttä ylläpitoon.</span>
          </div>
        </>
      );
    }
  }

  // Nextcloud stats
  let mem = false, cpu, numFiles, freeSpace;
  if (data.type === 'nextcloud') {
    mem = ((data.memTotal - data.memFree) / data.memTotal) * 100;
    cpu = data.cpu;
    freeSpace = round(data.freespace / 1024 / 1024 / 1024, 1) + " GB";
    numFiles = data.numFiles;
  }

  // Helper for rendering table rows
  const renderRow = (label, unit, value, calculated = {}) => (
    <tr>
      <th>
        {label} {unit && <span className="data-unit">({unit})</span>}
      </th>
      {showCalculated && <td>{calculated.min}</td>}
      {showCalculated && <td>{calculated.mean ?? calculated.avg}</td>}
      {showCalculated && <td>{calculated.max}</td>}
      <td>{value}</td>
    </tr>
  );

  // Save CSV handler
  const saveCSV = () => {
    const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const strippedData = rawValues.data.map(elem => {
      const time = moment(elem.timestamp, "x").format();
      const { ...rowTemp } = elem;
      return { time, ...rowTemp };
    });
    const ws = XLSX.utils.json_to_sheet(strippedData);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: fileType });
    let name = data.name || data.deviceid;
    name = name.replace(/[^a-zA-Z0-9]/g, "_");
    FileSaver.saveAs(blob, name + ".xlsx");
  };

  return (
    <>
      <CurrentValueModal
        isOpen={currentValueModalOpen}
        onClose={() => setCurrentValueModalOpen(false)}
        deviceid={data.deviceid}
        apikey={data.api}
        changeApi={props.changeApi}
      />

      <div className="device-titles">
        <h2>{data.name || data.deviceid}</h2>
        <button className="btn-save" onClick={changeDeviceName(data.deviceid, data.name)}>
          <EditIcon /> Nimeä
        </button>
        <button className="btn-save" onClick={toggleSensorFilter(data.deviceid)}>
          <HideIcon /> Piilota
        </button>
        <button className="btn-save" onClick={saveCSV}>
          <XlsxIcon /> Tallenna data
        </button>
        <button className="btn-save" onClick={() => setCurrentValueModalOpen(true)}>
          <ApiIcon /> Seuranta
        </button>
      </div>
      <div className="device-data">
        <table>
          <thead>
            <tr>
              <th></th>
              {showCalculated && <th>Minimi</th>}
              {showCalculated && <th>Mediaani</th>}
              {showCalculated && <th>Maksimi</th>}
              <th>{showCalculated ? <>Viimeisin arvo</> : null}</th>
            </tr>
          </thead>
          <tbody>
            {data.co2 && renderRow("CO2", "ppm", data.co2, rawValues.co2)}
            {data.pressure && renderRow(
              data.type === 'ruuvi' ? "Ilmanpaine" : "Paine-ero",
              "Pa",
              round(data.pressure, 1),
              {
                min: round(rawValues?.pressure?.min, 1),
                mean: round(rawValues?.pressure?.mean, 1),
                max: round(rawValues?.pressure?.max, 1)
              }
            )}
            {data.temperature && renderRow(
              "Lämpötila",
              "°C",
              <>
                {round(data.temperature, 1)}
                {data.temperature2 && <>/{round(data.temperature2, 1)}</>}
              </>,
              {
                min: round(rawValues.temperature?.min, 1) + (data.temperature2 ? "/" + round(rawValues?.temperature2?.min, 1) : ""),
                mean: round(rawValues.temperature?.mean, 1) + (data.temperature2 ? "/" + round(rawValues?.temperature2?.mean, 1) : ""),
                max: round(rawValues.temperature?.max, 1) + (data.temperature2 ? "/" + round(rawValues?.temperature2?.max, 1) : "")
              }
            )}
            {data.humidity && renderRow(
              "Kosteus",
              "%RH",
              <>
                {data.humidity}
                {data.humidity2 && <>/{data.humidity2}</>}
              </>,
              {
                min: rawValues.humidity?.min + (data.humidity2 ? "/" + rawValues?.humidity2?.min : ""),
                mean: rawValues.humidity?.mean + (data.humidity2 ? "/" + rawValues?.humidity2?.mean : ""),
                max: rawValues.humidity?.max + (data.humidity2 ? "/" + rawValues?.humidity2?.max : "")
              }
            )}
            {data.odometer && renderRow("Matkamittari", "km", data.odometer)}
            {mem && (
              <>
                {renderRow("Muistia käytössä", "%", round(mem, 1))}
                {renderRow("CPU-kuorma", "", round(cpu, 1))}
                {renderRow("Tiedostoja", "", numFiles)}
                {renderRow("Levytilaa jäljellä", "", freeSpace)}
              </>
            )}
            {data.type === "tasmotaplug" && (
              <>
                {renderRow("Teho", "W", data.power, rawValues.power)}
                {renderRow("Kulutus tänään", "kWh", data.today)}
                {renderRow("Kulutus eilen", "kWh", data.yesterday)}
              </>
            )}
            {showCalculated && data.type === "tasmotaplug" && (
              <>
                {renderRow("Virta", "A", data.current, rawValues.current)}
                {renderRow("Vaihesiirto", "φ", data.factor, rawValues.factor)}
                {renderRow("Loisteho", "var", data.reactivePower, rawValues.reactivePower)}
                {renderRow("Näennäisteho", "VA", data.apparentPower, rawValues.apparentPower)}
                {renderRow("Jännite", "V", data.voltage, rawValues.voltage)}
                <tr>
                  <th>Anturin ID</th>
                  <td>{data.deviceid}</td>
                </tr>
              </>
            )}
            {data.type === "Calibra" && (
              <>
                {renderRow("Compressor speed RPM", "", data["Compressor speed RPM"], rawValues["Compressor speed RPM"])}
                {renderRow("Brine in", "", data["Brine in"], rawValues["Brine in"])}
                {renderRow("Brine out", "", data["Brine out"], rawValues["Brine out"])}
                {renderRow("Tap water top", "", data["Tap water top"], rawValues["Tap water top"])}
                {renderRow("Tap water bottom", "", data["Tap water bottom"], rawValues["Tap water bottom"])}
                <tr>
                  <th>Condenser in temperature</th>
                  <td>{data['Condenser in temperature']}</td>
                </tr>
                <tr>
                  <th>Condenser out temperature</th>
                  <td>{data['Condenser out temperature']}</td>
                </tr>
                {renderRow("Outdoor temperature", "", data["Outdoor temperature"], rawValues["Outdoor temperature"])}
              </>
            )}
            <tr>
              <th className="device-date">Viimeisin mittaus</th>
              <td className="device-date">{lastMeasurement}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(Device);
